import React, { useCallback, useRef } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Portal,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export const fmtValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

export const visibleMeta = (meta = []) => meta.filter((item) => {
  const value = typeof item === 'string' ? item : item?.value;
  return value !== undefined && value !== null && String(value).trim() !== '';
});

const compactEvidenceRow = (row = {}) => {
  if (!row || typeof row !== 'object') return {};
  return {
    id: row.id,
    rowNumber: row.rowNumber,
    sourceRowKey: row.sourceRowKey,
    targetRowKey: row.targetRowKey,
    rowKey: row.rowKey,
    storageKey: row.storageKey,
    fileRowId: row.fileRowId,
    dealerName: row.dealerName,
    accountId: row.accountId,
    salesforceAccountId: row.salesforceAccountId,
    salesforceAccountName: row.salesforceAccountName,
    orderItemId: row.orderItemId || row.__orderItemId,
    matchedAppSheetOrderItemId: row.matchedAppSheetOrderItemId,
    referenceId: row.referenceId || row.referenceID,
    productName: row.productName || row.productDescription || row.productSelection,
    productCode: row.productCode || row.oemProductCode || row.normalizedProductCode || row.productCd,
    effectiveDate: row.effectiveDate,
    productSelectionDate: row.productSelectionDate,
    productSelectionDateDisplay: row.productSelectionDateDisplay,
    selectionDate: row.selectionDate,
    billingStartDate: row.billingStartDate,
    billingStartDateDisplay: row.billingStartDateDisplay,
    billingEndDate: row.billingEndDate,
    billingEndDateDisplay: row.billingEndDateDisplay,
    billingDate: row.billingDate,
    orderStartDate: row.orderStartDate,
    orderEndDate: row.orderEndDate,
    cancelDate: row.cancelDate,
    cancellationDate: row.cancellationDate,
    terminationDate: row.terminationDate,
    lastUpdatedDate: row.lastUpdatedDate,
    lastModified: row.lastModified,
    updatedAt: row.updatedAt,
    isBilling: row.isBilling,
    isBillable: row.isBillable,
    is_billing: row.is_billing,
    is_billable: row.is_billable,
    filler: row.filler,
    gmCost: row.gmCost,
    gmFee: row.gmFee,
    dealerPrice: row.dealerPrice,
    mrr: row.mrr,
    vendorCd: row.vendorCd,
    vendorCode: row.vendorCode,
    gmBrandCd: row.gmBrandCd,
    brandCode: row.brandCode,
    programName: row.programName,
    invoiceGroup: row.invoiceGroup,
    designation: row.designation,
    accountMatch: row.accountMatch,
  };
};

export const compactEvidenceContext = (context = {}) => ({
  source: context.source,
  sourceLabel: context.sourceLabel,
  productName: context.productName,
  productCode: context.productCode,
  price: context.price,
  status: context.status,
  orderItemId: context.orderItemId,
  sourceOrderItemId: context.sourceOrderItemId,
  matchedAppSheetOrderItemId: context.matchedAppSheetOrderItemId,
  referenceId: context.referenceId,
  rowKey: context.rowKey || context.sourceRowKey || context.row?.rowKey || context.row?.sourceRowKey || context.row?.storageKey || context.row?.fileRowId || context.row?.id || context.row?.rowNumber,
  sourceRowKey: context.sourceRowKey || context.row?.sourceRowKey || context.row?.rowKey || context.row?.storageKey || context.row?.fileRowId || context.row?.id || context.row?.rowNumber,
  targetRowKey: context.targetRowKey || context.row?.targetRowKey,
  salesforceAccountId: context.salesforceAccountId,
  salesforceAccountName: context.salesforceAccountName,
  bindingMethod: context.bindingMethod,
  bindingConfidence: context.bindingConfidence,
  row: compactEvidenceRow(context.row),
});

export const previewStyle = (draftPreview) => {
  if (!draftPreview) return {};
  if (draftPreview.variant === 'added') {
    return {
      bgcolor: 'success.lighter',
      borderColor: 'success.light',
      borderLeftColor: 'success.main',
      borderStyle: 'solid',
      opacity: 1,
    };
  }
  if (draftPreview.variant === 'removed') {
    return {
      bgcolor: 'transparent',
      borderColor: 'error.light',
      borderLeftColor: 'error.main',
      borderStyle: 'dashed',
      opacity: 0.58,
    };
  }
  if (draftPreview.variant === 'replaced' || draftPreview.variant === 'price') {
    return {
      bgcolor: 'warning.lighter',
      borderColor: 'warning.light',
      borderLeftColor: 'warning.main',
      borderStyle: 'solid',
      opacity: 1,
    };
  }
  return {
    bgcolor: 'info.lighter',
    borderColor: 'info.light',
    borderLeftColor: 'info.main',
    borderStyle: 'solid',
    opacity: 1,
  };
};

export const previewChipColor = (variant) => {
  if (variant === 'added') return 'success';
  if (variant === 'removed') return 'error';
  if (variant === 'price' || variant === 'replaced') return 'warning';
  return 'info';
};

export const KeyValueGrid = ({
  entries,
  gridSx = {},
  labelSx = {},
  valueSx = {},
  labelVariant = 'caption',
  valueVariant = 'caption',
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(140px, auto) 1fr',
      columnGap: 1.5,
      rowGap: 0.5,
      ...gridSx,
    }}
  >
    {entries.map(([k, v]) => (
      <React.Fragment key={k}>
        <Typography variant={labelVariant} sx={{ color: 'text.secondary', fontFamily: 'monospace', ...labelSx }}>
          {k}
        </Typography>
        <Typography variant={valueVariant} sx={{ wordBreak: 'break-word', ...valueSx }}>
          {fmtValue(v)}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

export const DetailHeader = ({ primary, secondary, actions, dragHandleProps }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    {dragHandleProps && (
      <Box
        {...dragHandleProps}
        sx={{
          cursor: 'grab',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          pt: 0.25,
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, lineHeight: 1 }}
      >
        Row details
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
        {primary}
      </Typography>
      {secondary && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {secondary}
        </Typography>
      )}
    </Box>
    <Stack direction="row" spacing={0}>
      {actions}
    </Stack>
  </Stack>
);

export const PinnedCard = ({ position, onDrag, onClose, primary, secondary, entries }) => {
  const dragStartRef = useRef(null);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startClient = { x: e.clientX, y: e.clientY };
    const startPos = { top: position.top, left: position.left };
    dragStartRef.current = { startClient, startPos };
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      onDrag({
        top: Math.max(8, startPos.top + (ev.clientY - startClient.y)),
        left: Math.max(8, Math.min(window.innerWidth - 200, startPos.left + (ev.clientX - startClient.x))),
      });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = prevUserSelect;
      dragStartRef.current = null;
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [position.left, position.top, onDrag]);

  return (
    <Portal>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex: (t) => t.zIndex.modal + 1,
          maxWidth: 460,
          minWidth: 320,
          p: 2,
          borderTop: '3px solid',
          borderColor: 'warning.main',
        }}
      >
        <Stack spacing={1.5}>
          <DetailHeader
            primary={primary}
            secondary={secondary}
            dragHandleProps={{ onPointerDown }}
            actions={
              <Tooltip title="Unpin and close">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <KeyValueGrid entries={entries} />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            Drag the handle to reposition - click another row to compare
          </Typography>
        </Stack>
      </Paper>
    </Portal>
  );
};
